import "twin.macro";

import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";

import { Close } from "@mui/icons-material";
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";

import { useQueryClient } from "@tanstack/react-query";

import { LoadingButton, Type } from "@features/ui";
import {
  useCreateUserMutation,
  usePaginatedUsersQuery,
  useUpdateUserMutation,
} from "@features/users/data";
import { Distributor } from "@models/Distributor";
import { User } from "@models/User";
import { ControlledTextInput } from "@utils/forms";
import { validEmailRegex } from "@utils/regex";
import useDebouncedValue from "@utils/useDebouncedValue";

import { distributorsKeyFactory } from "../data";

const ExistingUserPrompt = ({
  control,
  handleAddUserToDistributor,
  isPending,
  distUsers,
}: {
  control: any;
  handleAddUserToDistributor: (user: User) => void;
  isPending: boolean;
  distUsers: User[];
}) => {
  const email = useWatch({ name: "email", control });

  const debouncedEmail = useDebouncedValue(email, 300);
  const emailSearch =
    debouncedEmail === email && validEmailRegex.test(debouncedEmail)
      ? debouncedEmail
      : "";
  const { data: users } = usePaginatedUsersQuery(
    { filter: { nameOrEmail: emailSearch } },
    { enabled: !!emailSearch }
  );
  const match = users.find((user) => user.email === emailSearch);
  const isAlreadyAdded = distUsers.find((user) => user.email === emailSearch);
  if (isAlreadyAdded)
    return (
      <Alert severity="info">
        <Type.P tw="text-base!">
          {isAlreadyAdded.name} is already a member of this distributor
          {isAlreadyAdded.status === "inactive" &&
            `
              and is inactive. Please click "Show Inactive" and then add
              them back`}
          .
        </Type.P>
      </Alert>
    );
  if (!match) return null;
  if (match.role !== "orderer")
    return (
      <Type.P>
        {match.name} already exists as a {match.role}
      </Type.P>
    );
  return (
    <Alert severity="info">
      <Type.P tw="text-base!">
        {match.name} is already a member of this organization
        {match.status === "inactive" && " and is inactive"}. Would you like to
        add them to this distributor?
      </Type.P>

      <LoadingButton
        tw="mt-2"
        variant="contained"
        onClick={() => {
          handleAddUserToDistributor(match);
        }}
        loading={isPending}
      >
        Add {match.status === "inactive" ? "and activate" : ""} user
      </LoadingButton>
    </Alert>
  );
};

const NewDistributorUserModal = ({
  onClose,
  distributor,
}: {
  onClose: () => void;
  distributor: Distributor;
}) => {
  const createUserMutation = useCreateUserMutation();
  const updateUserMutation = useUpdateUserMutation();
  const queryClient = useQueryClient();

  const { name, id } = useSelector(
    (state: any) => state.currentUser.organization
  );
  const { control, handleSubmit } = useForm({
    defaultValues: { email: "", name: "" },
  });

  const handleAddUserToDistributor = (user: User) => {
    updateUserMutation.mutate(
      {
        id: user.id,
        territories: user.territories,
        channels: user.channelUsers.map((cu) => cu.channel),
        brands: user.limitedBrands,
        distributorId: distributor.id,
        distributorOrganizationIds: [user.organizationId],
        status: "active",
      },
      {
        onSuccess: async () => {
          queryClient
            .invalidateQueries({
              queryKey: distributorsKeyFactory.detail(distributor.id).queryKey,
            })
            .then(() => onClose());
        },
      }
    );
  };

  const handleCreateUser = (data) => {
    createUserMutation.mutate(
      {
        name: data.name,
        email: data.email,
        distributorId: distributor.id,
        role: "orderer",
        distributorOrganizationIds: [id],
      },
      {
        onSuccess: () => {
          onClose();
        },
      }
    );
  };

  return (
    <Dialog open onClose={onClose} fullWidth>
      <DialogTitle tw="flex justify-between items-start">
        New Distributor User
        <IconButton onClick={onClose} edge="end">
          <Close />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit(handleCreateUser)}>
        <DialogContent tw="space-y-6 pt-0">
          <ControlledTextInput
            name="email"
            control={control}
            label="Email"
            rules={{ required: "Email is required" }}
          />
          <ExistingUserPrompt
            control={control}
            handleAddUserToDistributor={handleAddUserToDistributor}
            isPending={updateUserMutation.isPending}
            distUsers={distributor.orderers}
          />
          <ControlledTextInput
            name="name"
            control={control}
            label="Name"
            rules={{ required: "Name is required" }}
          />
          <Type.P>This user will be added to {name}</Type.P>
          {createUserMutation.isError && (
            <Alert severity="error">{createUserMutation.error.message}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={createUserMutation.isPending}
          >
            Create User
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default NewDistributorUserModal;
